import { AuthChoice } from '../data/AuthChoice';
import {
	GenericRequest,
	HeadersJson,
	HTTPFetchResponse,
	SsoCredentialType,
	UserNameDomainCredentials,
} from '../data/AuthTypes';
import {
	Authenticator,
	AuthenticateError,
	analyticsConstants,
	logEvent,
} from './Authenticator';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import { ConnectionSecurityMode, config } from '../../../auth/AuthConfig';
import { PrimaryTokenFetchFailure } from '../../../auth/AuthErrors';
import { base64Encode } from '../../../utils/Encoder';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Severity } from '@sentry/browser';
import DSAuth from '../DSAuth';
import { headerConstants, stringConstants } from '../../../auth/AuthConstants';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';
import { environment } from '../../../auth/environment';

export default class HttpBasicAuthenticator implements Authenticator {
	private readonly HttpBasicChallengeHeaderValueStart = 'basic realm=';
	private readonly AuthorizationProtocolHeader = 'Authorization: Basic ';

	async getToken(
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		if (
			authProtocol.getLocation().startsWith('http:') &&
			config.connectionSecurityMode === ConnectionSecurityMode.SecureOnly
		) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`The HTTP Basic URL is not considered secure. Check the ConnectionSecurityMode policy registry key allows HTTP as well as HTTPS`,
				AotCategory.Error
			);

			throw new PrimaryTokenFetchFailure(new Error('DisallowedBySecurityPolicy'));
		}

		const credentials = await environment.getSsoCredentials(
			SsoCredentialType.UserNameDomain
		);
		if (!credentials) {
			Logger.getInstance().writeAot(
				AotCategory.Info,
				`SSO credentials not retrieved, skipping HTTP Basic authentication`
			);
			return AuthenticateError.UnsuitableProtocol;
		}

		SentryLogger.getInstance().addBreadCrumb({
			category: 'HttpBasicAuthenticator',
			level: Severity.Info,
			message: 'getToken for HttpBasicAuthenticator',
		});

		await this.makeInitialRequestToHttpBasic(
			authProtocolsRequest.body,
			authProtocol.getLocation(),
			transactionId,
			referenceUri,
			id
		);

		return await this.fetchPrimaryToken(
			authProtocolsRequest.body,
			authProtocol.getLocation(),
			transactionId,
			referenceUri,
			id,
			credentials
		);
	}

	/**
	 Make the initial request with no auth header.
	 We expect to get a WWW-Authenticate header of the form:
	   WWW-Authenticate: Basic realm="The Name Of The Realm - i.e. The StoreFront tenant GUID"
	 */
	private async makeInitialRequestToHttpBasic(
		body: string,
		protocolLocation: string,
		transactionId: string,
		referenceUri: string,
		id: string
	) {
		const headers = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
				[headerConstants.CONTENT_TYPE]: headerConstants.TOKEN_REQUEST_CONTENT_TYPE,
			},
			transactionId
		);

		let response;
		try {
			response = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				protocolLocation,
				headers,
				body,
				referenceUri
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}

		if (response.statusCode !== HttpStatusCodes.HTTP_STATUS_DENIED) {
			const error = `The 401 challenge status code was not received for initial HTTP Basic request. Instead the server responded with the status code ${response.statusCode}`;
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: ${error}`,
				AotCategory.Error,
				error
			);
			throw new PrimaryTokenFetchFailure(new Error(error));
		}

		this.validateHeader(response.headers);
	}

	private validateHeader(headers: HeadersJson) {
		let wwwAuthenticateHeaderValue = null;
		if (headers) {
			for (const key in headers) {
				if (
					key.toLowerCase() === headerConstants.AUTHENTICATE_HEADER.toLowerCase() &&
					headers[key].toLowerCase().startsWith(this.HttpBasicChallengeHeaderValueStart)
				) {
					wwwAuthenticateHeaderValue = headers[key];
					break;
				}
			}
		}

		if (!wwwAuthenticateHeaderValue) {
			const error = `Valid WWW-Authenticate header was not found in the HTTP Basic response`;
			Logger.getInstance().write(logConstants.ERROR, error, AotCategory.Error);
			throw new PrimaryTokenFetchFailure(new Error(error));
		}

		const realm = wwwAuthenticateHeaderValue.match(/"([^"]*)"/)?.[1];
		if (!realm) {
			const error = `Invalid HTTP Basic realm value`;
			Logger.getInstance().write(logConstants.ERROR, error, AotCategory.Error);
			throw new PrimaryTokenFetchFailure(new Error(error));
		}

		Logger.getInstance().write(logConstants.INFO, `HTTP Basic realm: ${realm}`);
	}

	private async fetchPrimaryToken(
		body: string,
		protocolLocation: string,
		transactionId: string,
		referenceUri: string,
		id: string,
		credentials: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		const headers = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
				[headerConstants.CONTENT_TYPE]: headerConstants.TOKEN_REQUEST_CONTENT_TYPE,
				[this.AuthorizationProtocolHeader]: await this.createAuthorizationHeaderValue(
					credentials
				),
			},
			transactionId
		);

		let response;
		try {
			response = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				protocolLocation,
				headers,
				body,
				referenceUri
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}

		if (response.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
			logEvent(analyticsConstants.LABEL_HTTPBASICAUTHENTICATOR, 1);
			return response;
		}

		const error = `Failed to fetch primary token - unexpected HTTP response status code: ${response.statusCode}`;
		Logger.getInstance().write(
			logConstants.ERROR,
			`${id}: ${error}`,
			AotCategory.Error,
			error
		);
		throw new PrimaryTokenFetchFailure(new Error(error));
	}

	private async createAuthorizationHeaderValue(credentials: string): Promise<string> {
		try {
			const parsedCredentials: UserNameDomainCredentials = JSON.parse(credentials);

			if (!parsedCredentials.userName || !parsedCredentials.password) {
				throw new Error('Username or password not found in credentials');
			}

			let fullUserName = parsedCredentials.userName;
			if (parsedCredentials.domain?.length > 0) {
				fullUserName = `${parsedCredentials.domain}\\${parsedCredentials.userName}`;
			}

			return base64Encode(`${fullUserName}:${parsedCredentials.password}`);
		} catch (e) {
			const error = `Failed to create authorization header from credentials: ${e.message}`;
			Logger.getInstance().write(logConstants.ERROR, error, AotCategory.Error);
			throw new PrimaryTokenFetchFailure(new Error(error));
		}
	}
}
